import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  cognitoGetCurrentUser,
  cognitoGetSession,
  cognitoSignOut,
} from "shared/services/aws/cognito";
import { apiLogout, apiRefresh } from "../../services/api";

/* ================================
   TIPOS
================================ */

export type PerfilUsuario = "comprador" | "vendedor" | "admin";
interface UserProps {
  uid: string;
  email?: string | null;
  name: string | null;
  roles: string[];
  perfil: PerfilUsuario[];
  imageUser?: string | null;
}

type AuthContextData = {
  signed: boolean;
  loadingAuth: boolean;
  user: UserProps | null;
  perfilAtivo: PerfilUsuario;
  setPerfilAtivo: (perfil: PerfilUsuario) => void;
  isVendedor: boolean;
  isAdmin: boolean;
  addPerfilVendedor: () => Promise<void>;
  atualizarUsuario: (data: Partial<UserProps>) => void;
  logout: () => Promise<void>;
};

/* ================================
   CONTEXT
================================ */

export const AuthContext = createContext({} as AuthContextData);

/* ================================
   PROVIDER
================================ */

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProps | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [perfilAtivo, setPerfilAtivoState] =
    useState<PerfilUsuario>("comprador");
  const isVendedor = user?.perfil.includes("vendedor") ?? false;
  const isAdmin = user?.roles?.includes("ADMIN") ?? false;

  /* ================================
     RESTAURAR PERFIL
  ================================ */

  useEffect(() => {
    const perfilSalvo = localStorage.getItem(
      "perfilAtivo",
    ) as PerfilUsuario | null;
    if (perfilSalvo) {
      setPerfilAtivoState(perfilSalvo);
    }
  }, []);

  const setPerfilAtivo = (perfil: PerfilUsuario) => {
    localStorage.setItem("perfilAtivo", perfil);
    setPerfilAtivoState(perfil);
  };

  const clearAuthState = () => {
    setUser(null);
    setPerfilAtivoState("comprador");
    localStorage.removeItem("user");
    localStorage.removeItem("perfilAtivo");
    localStorage.removeItem("userName");
    localStorage.removeItem("apiAccessToken");
    localStorage.removeItem("apiRefreshToken");
    localStorage.removeItem("cognito-token");
    sessionStorage.clear();
  };

  /* ================================
     🔥 ATUALIZAR USER
  ================================ */

  const atualizarUsuario = (data: Partial<UserProps>) => {
    setUser((prev) => {
      if (!prev) return prev;

      const atualizado = { ...prev, ...data };

      localStorage.setItem("user", JSON.stringify(atualizado));
      return atualizado;
    });
  };

  /* ================================
     🔥 CARREGAR USUÁRIO
  ================================ */

  useEffect(() => {
    const localUser = localStorage.getItem("user");
    if (localUser) {
      setUser(JSON.parse(localUser));
    }

    async function loadUser() {
      try {
        const session = await cognitoGetSession();

        if (!session.tokens) {
          clearAuthState();

          return;
        }

        const cognitoUser = await cognitoGetCurrentUser();

        /* ================================
           🔥 IGNORA GUEST
        ================================ */

        if (
          cognitoUser.signInDetails?.loginId ===
          process.env.REACT_APP_MOVE_GUEST
        ) {
          clearAuthState();

          return;
        }
        const apiToken = localStorage.getItem("apiAccessToken");
        const refreshToken = localStorage.getItem("apiRefreshToken");
        let payload: { roles?: string[] };

        if (!apiToken || !refreshToken) {
          clearAuthState();
          await cognitoSignOut();

          return;
        }

        try {
          payload = JSON.parse(atob(apiToken.split(".")[1]));
        } catch {
          clearAuthState();
          await cognitoSignOut();

          return;
        }
        const userData: UserProps = {
          uid: cognitoUser.userId,
          email: cognitoUser.signInDetails?.loginId ?? null,
          name: localStorage.getItem("userName") ?? "Usuário",
          roles: payload?.roles ?? [],
          perfil: [
            "comprador",
            ...(payload?.roles?.includes("STORE_OWNER") ? ["vendedor" as const] : []),
            ...(payload?.roles?.includes("ADMIN") ? ["admin" as const] : []),
          ],
          imageUser: null,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        const perfilSalvo = localStorage.getItem(
          "perfilAtivo",
        ) as PerfilUsuario | null;
        if (perfilSalvo && userData.perfil.includes(perfilSalvo)) {
          setPerfilAtivoState(perfilSalvo);
        } else {
          setPerfilAtivoState("comprador");
        }
      } catch (error) {
        console.error("Erro ao carregar usuário:", error);

        setUser(null);
        localStorage.removeItem("user");
      } finally {
        setLoadingAuth(false);
      }
    }
    loadUser();
  }, []);

  /* ================================
     🔥 ADD PERFIL VENDEDOR
  ================================ */

  const addPerfilVendedor = async () => {
    if (!user) return;
    if (user.perfil.includes("vendedor")) return;
    const novoPerfil: PerfilUsuario[] = [...user.perfil, "vendedor"];
    const atualizado = {
      ...user,
      perfil: novoPerfil,
    };
    await apiRefresh();
    setUser(atualizado);
    localStorage.setItem("user", JSON.stringify(atualizado));
  };

  /* ================================
     USER LOGOUT
  ================================ */

  const logout = async () => {
    setUser(null);
    await apiLogout();
  };

  /* ================================
     PROVIDER
  ================================ */

  return (
    <AuthContext.Provider
      value={{
        signed: !!user,
        loadingAuth,
        user,
        perfilAtivo,
        setPerfilAtivo,
        isVendedor,
        isAdmin,
        addPerfilVendedor,
        atualizarUsuario,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/* ================================
   HOOK
================================ */

export const useAuth = () => useContext(AuthContext);
